import { customColor } from '@brandclub/bc-widgets';
import type { ColorValue, EditField } from '@brandclub/bc-widgets';

/**
 * One shared widget id for the whole support-ticket card family (create/list/
 * status). They are one visual family, so a brand edits them once and all three
 * stay consistent.
 *
 * Note: the ticket *state pill* is deliberately NOT editable here. Its open/
 * submitted colors already come from Concierge Studio's Support branding via
 * `--bc-support-bg` / `--bc-support-text` (D-33), and the resolved / waiting /
 * in-progress colors carry semantic meaning rather than branding.
 */
export const SUPPORT_TICKET_CARD_WIDGET_ID = 'concierge-support-ticket-card';

export const SUPPORT_TICKET_CARD_DISPLAY_NAME = 'Support Ticket Cards';

/**
 * Defaults are the hex equivalents of the Concierge Tailwind palette these
 * cards used before becoming editable (the palette is defined in oklch in
 * `styles/compiled.css.ts`): gray-200 #e5e5e5, gray-400 #aeaeae,
 * gray-500 #8f8f8f, gray-700 #555555, gray-800 #3a3a3a, gray-900 #181d56,
 * gray-50 #f8f8f8. Rendering is therefore unchanged until a brand edits.
 */
export const supportTicketCardDefaultState = {
  // Literal hexes, NOT theme colors: these cards were styled with the Tailwind
  // palette, which is independent of the widget theme. Defaulting them to
  // `themeColor(...)` would silently restyle already-configured brands (e.g.
  // gray-900 #181d56 -> PetArmor's textPrimary #2B4168). A brand that wants
  // them on-theme can pick a theme color in the panel.
  cardBgColor: customColor('#ffffff'),
  cardBorderColor: customColor('#e5e5e5'),
  cardBorderRadius: 12,
  cardPadding: 16,
  listCardPadding: 12,

  labelColor: customColor('#8f8f8f'),
  refColor: customColor('#181d56'),
  refFontSize: 16,
  listRefFontSize: 14,

  metaColor: customColor('#8f8f8f'),
  metaFontSize: 12,
  countColor: customColor('#aeaeae'),

  bodyColor: customColor('#555555'),
  bodyFontSize: 14,

  viewBgColor: customColor('#f8f8f8'),
  viewBorderColor: customColor('#e5e5e5'),
  viewTextColor: customColor('#555555'),
  viewBorderRadius: 6,
  viewFontSize: 12,

  replyBgColor: customColor('#f8f8f8'),
  replyBorderColor: customColor('#e5e5e5'),
  replyTextColor: customColor('#3a3a3a'),
};

export type SupportTicketCardState = typeof supportTicketCardDefaultState;

export const supportTicketCardEditFields: EditField[] = [
  { key: 'cardBgColor', label: 'Card Background', type: 'color', group: 'Card' },
  { key: 'cardBorderColor', label: 'Card Border', type: 'color', group: 'Card' },
  { key: 'cardBorderRadius', label: 'Card Radius (px)', type: 'number', min: 0, max: 24, step: 2, group: 'Card' },
  { key: 'cardPadding', label: 'Card Padding (px)', type: 'number', min: 8, max: 32, step: 2, group: 'Card' },
  { key: 'listCardPadding', label: 'Ticket List Padding (px)', type: 'number', min: 8, max: 32, step: 2, group: 'Card' },

  { key: 'labelColor', label: '"Ticket" Label Color', type: 'color', group: 'Header' },
  { key: 'refColor', label: 'Ticket Number Color', type: 'color', group: 'Header' },
  { key: 'refFontSize', label: 'Ticket Number Size (px)', type: 'number', min: 12, max: 24, step: 1, group: 'Header' },
  { key: 'listRefFontSize', label: 'List Ticket Number Size (px)', type: 'number', min: 10, max: 20, step: 1, group: 'Header' },

  { key: 'metaColor', label: 'Secondary Text Color', type: 'color', group: 'Text' },
  { key: 'metaFontSize', label: 'Secondary Text Size (px)', type: 'number', min: 10, max: 16, step: 1, group: 'Text' },
  { key: 'countColor', label: 'Ticket Count Color', type: 'color', group: 'Text' },
  { key: 'bodyColor', label: 'Body Text Color', type: 'color', group: 'Text' },
  { key: 'bodyFontSize', label: 'Body Text Size (px)', type: 'number', min: 12, max: 20, step: 1, group: 'Text' },

  { key: 'viewBgColor', label: 'View Button Background', type: 'color', group: 'View Button' },
  { key: 'viewBorderColor', label: 'View Button Border', type: 'color', group: 'View Button' },
  { key: 'viewTextColor', label: 'View Button Text', type: 'color', group: 'View Button' },
  { key: 'viewBorderRadius', label: 'View Button Radius (px)', type: 'number', min: 0, max: 20, step: 2, group: 'View Button' },
  { key: 'viewFontSize', label: 'View Button Font Size (px)', type: 'number', min: 10, max: 16, step: 1, group: 'View Button' },

  { key: 'replyBgColor', label: 'Reply Block Background', type: 'color', group: 'Latest Reply' },
  { key: 'replyBorderColor', label: 'Reply Block Accent', type: 'color', group: 'Latest Reply' },
  { key: 'replyTextColor', label: 'Reply Text Color', type: 'color', group: 'Latest Reply' },
];

/** Fully resolved values the three support cards render with. */
export interface SupportTicketCardStyles {
  cardBgColor: string;
  cardBorderColor: string;
  cardBorderRadius: number;
  cardPadding: number;
  listCardPadding: number;
  labelColor: string;
  refColor: string;
  refFontSize: number;
  listRefFontSize: number;
  metaColor: string;
  metaFontSize: number;
  countColor: string;
  bodyColor: string;
  bodyFontSize: number;
  viewBgColor: string;
  viewBorderColor: string;
  viewTextColor: string;
  viewBorderRadius: number;
  viewFontSize: number;
  replyBgColor: string;
  replyBorderColor: string;
  replyTextColor: string;
}

type ResolveColor = (value: ColorValue | string | undefined | null, defaultHex?: string) => string;

export const buildSupportTicketCardStyles = (
  state: SupportTicketCardState,
  resolveColor: ResolveColor,
): SupportTicketCardStyles => ({
  cardBgColor: resolveColor(state.cardBgColor, '#ffffff'),
  cardBorderColor: resolveColor(state.cardBorderColor, '#e5e5e5'),
  cardBorderRadius: Number(state.cardBorderRadius),
  cardPadding: Number(state.cardPadding),
  listCardPadding: Number(state.listCardPadding),
  labelColor: resolveColor(state.labelColor, '#8f8f8f'),
  refColor: resolveColor(state.refColor, '#181d56'),
  refFontSize: Number(state.refFontSize),
  listRefFontSize: Number(state.listRefFontSize),
  metaColor: resolveColor(state.metaColor, '#8f8f8f'),
  metaFontSize: Number(state.metaFontSize),
  countColor: resolveColor(state.countColor, '#aeaeae'),
  bodyColor: resolveColor(state.bodyColor, '#555555'),
  bodyFontSize: Number(state.bodyFontSize),
  viewBgColor: resolveColor(state.viewBgColor, '#f8f8f8'),
  viewBorderColor: resolveColor(state.viewBorderColor, '#e5e5e5'),
  viewTextColor: resolveColor(state.viewTextColor, '#555555'),
  viewBorderRadius: Number(state.viewBorderRadius),
  viewFontSize: Number(state.viewFontSize),
  replyBgColor: resolveColor(state.replyBgColor, '#f8f8f8'),
  replyBorderColor: resolveColor(state.replyBorderColor, '#e5e5e5'),
  replyTextColor: resolveColor(state.replyTextColor, '#3a3a3a'),
});
